/**
 * AdminDeals.jsx — Admin deal management dashboard.
 *
 * Features:
 * - List all deals with payment status
 * - Release funds (full or milestone)
 * - Close deal (COMPLETED → CLOSED)
 * - Platform earnings summary
 * - Warn when bidder has no payout profile
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider,
  Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, Snackbar, Collapse,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PaymentIcon from '@mui/icons-material/Payment';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { apiClient } from '../../utils/auth';

const DEAL_STATUS_COLORS = {
  ACTIVE: 'info', IN_PROGRESS: 'warning', COMPLETED: 'success', CLOSED: 'default',
};
const PAY_STATUS_COLORS = {
  NOT_INITIATED: 'default', ORDER_CREATED: 'warning',
  CAPTURED: 'info', RELEASED: 'success', DISPUTE_RESOLVED: 'success',
};

function fmt(n) {
  return Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

export default function AdminDeals() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const role = localStorage.getItem('cmp_role');
    if (role !== 'ADMIN') {
      if (role === 'ARBITRATOR') {
        navigate('/arbitrations/mine');
      } else if (role === 'BUYER') {
        navigate('/buyer/requirements');
      } else if (role === 'BIDDER') {
        navigate('/bidder/requirements');
      } else {
        navigate('/login');
      }
    }
  }, [navigate]);

  // Release dialog state
  const [releaseDialog, setReleaseDialog] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState('');

  // Payout done state
  const [payoutDone, setPayoutDone] = useState({}); // { dealId: {loading, ref} }

  // Close deal dialog
  const [closeDialog, setCloseDialog] = useState(null);
  const [closing, setClosing] = useState(false);

  // NEFT verification dialog
  const [neftDialog, setNeftDialog] = useState(null);
  const [neftRejectReason, setNeftRejectReason] = useState('');
  const [neftVerifying, setNeftVerifying] = useState(false);
  const [neftError, setNeftError] = useState('');

  const [revealingId, setRevealingId] = useState(null);

  // Snackbar for simulation results
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/v1/admin/deals/');
      setDeals(Array.isArray(data.deals) ? data.deals : []);
      setSummary(data.summary || null);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load deals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleRelease = async (force = false) => {
    if (!releaseDialog) return;
    setReleasing(true); setReleaseError('');
    try {
      const { deal, type, milestone } = releaseDialog;
      const body = force ? { force: true } : {};
      if (type === 'full') {
        await apiClient.post(`/api/v1/admin/payment/deals/${deal.id}/release/`, body);
      } else {
        await apiClient.post(`/api/v1/admin/payment/deals/${deal.id}/milestone/${milestone.id}/release/`, body);
      }
      setReleaseDialog(null);
      await fetchDeals();
    } catch (err) {
      const errData = err.response?.data?.error;
      if (errData?.code === 'RETENTION_PERIOD_ACTIVE' && errData?.details?.can_force) {
        setReleaseError(`RETENTION_ACTIVE:${errData.details.eligible_at}`);
      } else {
        setReleaseError(errData?.message || 'Release failed.');
      }
    } finally {
      setReleasing(false);
    }
  };

  const handleRevealIdentity = async (dealId) => {
    setRevealingId(dealId);
    try {
      await apiClient.post(`/api/v1/admin/deals/${dealId}/reveal/`);
      showSnack(`Identity revealed for Deal #${dealId}. Both parties have been notified.`);
      await fetchDeals();
    } catch (err) {
      showSnack(err.response?.data?.error?.message || 'Failed to reveal identity.', 'error');
    } finally {
      setRevealingId(null);
    }
  };

  const handleClose = async () => {
    if (!closeDialog) return;
    setClosing(true);
    try {
      await apiClient.patch(`/api/v1/deals/${closeDialog.id}/status/`, { deal_status: 'CLOSED' });
      setCloseDialog(null);
      await fetchDeals();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to close deal.');
      setCloseDialog(null);
    } finally {
      setClosing(false);
    }
  };

  const handleNeftVerify = async (action) => {
    if (!neftDialog) return;
    const reason = neftRejectReason.trim();
    if (action === 'REJECT' && !reason) {
      setNeftError('Rejection reason is required.');
      return;
    }
    setNeftVerifying(true);
    setNeftError('');
    try {
      const body = action === 'REJECT'
        ? { action: 'REJECT', rejection_reason: reason }
        : { action: 'APPROVE' };
      const { data } = await apiClient.post(
        `/api/v1/admin/payment/deals/${neftDialog.id}/verify-neft/`,
        body,
      );
      showSnack(data.message || (action === 'APPROVE' ? 'NEFT transfer verified.' : 'NEFT transfer rejected.'));
      setNeftDialog(null);
      setNeftRejectReason('');
      await fetchDeals();
    } catch (err) {
      setNeftError(err.response?.data?.error?.message || 'NEFT verification failed.');
    } finally {
      setNeftVerifying(false);
    }
  };

  const isNeftPendingReview = (deal, paymentStatus) => (
    deal.payment_method === 'NEFT'
    && paymentStatus === 'ORDER_CREATED'
    && deal.neft_details
    && deal.neft_details.verification_status !== 'REJECTED'
  );

  const handleMarkPayoutDone = async (dealId) => {
    setPayoutDone(prev => ({ ...prev, [dealId]: { loading: true, ref: null } }));
    try {
      const { data } = await apiClient.post(`/api/v1/admin/payment/deals/${dealId}/payout-done/`);
      setPayoutDone(prev => ({ ...prev, [dealId]: { loading: false, ref: data.payout_reference } }));
      await fetchDeals();
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to mark payout done.');
      setPayoutDone(prev => ({ ...prev, [dealId]: { loading: false, ref: null } }));
    }
  };

  const handleSimulatePayout = async (dealId) => {
    setPayoutDone(prev => ({ ...prev, [dealId]: { loading: true, ref: null } }));
    try {
      const { data } = await apiClient.post(`/api/v1/admin/payment/deals/${dealId}/simulate-payout/`);
      const suffix = data.used_mock_fallback ? ' (Mock fallback — Razorpay X not active)' : ' via Razorpay';
      showSnack(`✓ ${data.message}${suffix} · Ref: ${data.payout_reference}`);
      setPayoutDone(prev => ({ ...prev, [dealId]: { loading: false, ref: data.payout_reference } }));
      await fetchDeals();
    } catch (err) {
      showSnack(err.response?.data?.error?.message || 'Simulation failed.', 'error');
      setPayoutDone(prev => ({ ...prev, [dealId]: { loading: false, ref: null } }));
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Admin — Deal Management</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined" color="primary"
            onClick={() => navigate('/admin/arbitrators')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Arbitrators Review
          </Button>
          <Button
            variant="outlined" color="error" startIcon={<GavelIcon />}
            onClick={() => navigate('/admin/disputes')}
            id="btn-disputes-page"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Manage Disputes
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Razorpay Simulation Info Banner */}
      <Paper
        variant="outlined"
        sx={{
          mb: 3, p: 2,
          background: 'rgba(124,58,237,0.06)',
          borderColor: 'rgba(124,58,237,0.28)',
          borderRadius: 2,
          display: 'flex', alignItems: 'flex-start', gap: 1.5,
        }}
      >
        <PaymentIcon sx={{ color: '#a78bfa', mt: 0.25, flexShrink: 0 }} />
        <Box>
          <Typography variant="subtitle2" color="#a78bfa" fontWeight={700} gutterBottom>
            Razorpay Payout Simulation — Admin Guide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            After releasing funds (<Chip label="RELEASED" size="small" color="success" sx={{ mx: 0.5, fontSize: 11 }} />),
            use <strong>Simulate Payout</strong> to trigger a dummy Razorpay bank transfer to the bidder's configured dummy account.
            If no payout profile is configured or Razorpay X is unavailable, a mock reference is generated automatically.
            The bidder sees the transfer status in their <strong>My Earnings</strong> dashboard.
          </Typography>
        </Box>
      </Paper>

      {/* Platform earnings summary */}
      {summary && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Captured</Typography>
            <Typography variant="h6">Rs.{fmt(summary.total_captured)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Released to Bidders</Typography>
            <Typography variant="h6">Rs.{fmt(summary.total_released)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Platform Fees Earned (5%)</Typography>
            <Typography variant="h6" color="success.main">Rs.{fmt(summary.total_platform_fees)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Funds Held (Pending Release)</Typography>
            <Typography variant="h6" color="warning.main">Rs.{fmt(summary.total_held)}</Typography>
          </Box>
        </Paper>
      )}

      {/* Deals table */}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.default' }}>
              <TableCell><strong>Deal</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
              <TableCell><strong>Deal Status</strong></TableCell>
              <TableCell><strong>Identity</strong></TableCell>
              <TableCell><strong>Payment</strong></TableCell>
              <TableCell><strong>Payout Profile</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deals.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No deals found.</Typography>
                </TableCell>
              </TableRow>
            )}
            {deals.map((deal) => {
              const ps = deal.payment_status || 'NOT_INITIATED';
              const canRelease = ps === 'CAPTURED' && !deal.has_active_dispute && deal.bidder_has_payout_profile;
              const canClose = deal.deal_status === 'COMPLETED';
              const needsPayoutProfile = ps === 'CAPTURED' && !deal.bidder_has_payout_profile;
              const milestones = deal.milestones || [];
              const releasableMilestones = milestones.filter(
                m => m.milestone_status === 'CONFIRMED' && !deal.has_active_dispute && deal.bidder_has_payout_profile
              );

              return (
                <TableRow key={deal.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>#{deal.id}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {deal.requirement_category || `Req #${deal.requirement_id}`}
                    </Typography>
                  </TableCell>
                  <TableCell>Rs.{fmt(deal.deal_value)}</TableCell>
                  <TableCell>
                    <Chip label={deal.deal_status} color={DEAL_STATUS_COLORS[deal.deal_status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    {deal.anonymity_release_flag ? (
                      <Chip label="Revealed" color="success" size="small" />
                    ) : deal.identity_reveal_request_count > 0 ? (
                      <Chip label={`${deal.identity_reveal_request_count} request(s)`} color="warning" size="small" />
                    ) : (
                      <Chip label="Anonymous" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={ps} color={PAY_STATUS_COLORS[ps] || 'default'} size="small" />
                    {deal.payment_method === 'NEFT' && (
                      <Chip label="NEFT" color="secondary" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                    )}
                    {isNeftPendingReview(deal, ps) && (
                      <Chip label="NEFT pending review" color="warning" size="small" variant="outlined" sx={{ ml: 0.5, mt: 0.5 }} />
                    )}
                    {deal.payment_method === 'NEFT' && deal.neft_details?.verification_status === 'REJECTED' && (
                      <Chip label="NEFT rejected" color="error" size="small" variant="outlined" sx={{ ml: 0.5, mt: 0.5 }} />
                    )}
                    {deal.payment_method === 'NEFT' && deal.neft_details?.verification_status === 'APPROVED' && (
                      <Chip label="NEFT verified" color="success" size="small" variant="outlined" sx={{ ml: 0.5, mt: 0.5 }} />
                    )}
                    {deal.has_active_dispute && (
                      <Chip label="Dispute open" color="error" size="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {deal.bidder_has_payout_profile
                      ? (
                        <Box>
                          <Chip icon={<CheckCircleIcon />} label="Set up" color="success" size="small" />
                          {deal.bidder_payout_verification_status && (
                            <Chip
                              label={deal.bidder_payout_verification_status}
                              size="small"
                              color={deal.bidder_payout_verification_status === 'VERIFIED' ? 'success'
                                : deal.bidder_payout_verification_status === 'REJECTED' ? 'error' : 'warning'}
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </Box>
                      )
                      : <Chip icon={<WarningIcon />} label="Not set up" color="warning" size="small" />}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {!deal.anonymity_release_flag && deal.identity_reveal_request_count > 0 && (
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={revealingId === deal.id ? <CircularProgress size={14} color="inherit" /> : <VisibilityIcon />}
                          disabled={revealingId === deal.id}
                          onClick={() => handleRevealIdentity(deal.id)}
                        >
                          Approve Reveal
                        </Button>
                      )}
                      {/* NEFT verification — pending admin review */}
                      {isNeftPendingReview(deal, ps) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<AccountBalanceIcon />}
                          onClick={() => {
                            setNeftError('');
                            setNeftRejectReason('');
                            setNeftDialog(deal);
                          }}
                        >
                          Verify NEFT
                        </Button>
                      )}
                      {/* NEFT details — already reviewed */}
                      {deal.payment_method === 'NEFT' && deal.neft_details && !isNeftPendingReview(deal, ps) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<AccountBalanceIcon />}
                          onClick={() => {
                            setNeftError('');
                            setNeftRejectReason('');
                            setNeftDialog(deal);
                          }}
                        >
                          View NEFT
                        </Button>
                      )}
                      {/* Full release — non-escrow deals */}
                      {canRelease && milestones.length === 0 && (
                        <Button size="small" variant="contained" color="success"
                          onClick={() => setReleaseDialog({ deal, type: 'full' })}>
                          Release Funds
                        </Button>
                      )}
                      {/* Milestone releases — shown per confirmed milestone */}
                      {milestones.length > 0 && (
                        <Box sx={{ width: '100%' }}>
                          {milestones.map(ms => {
                            const canReleaseMilestone = ms.milestone_status === 'CONFIRMED'
                              && !deal.has_active_dispute
                              && deal.bidder_has_payout_profile;
                            const fee = (Number(ms.amount) * 0.05).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                            const net = (Number(ms.amount) * 0.95).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                            return (
                              <Box key={ms.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ minWidth: 20, fontWeight: 600 }}>M{ms.sequence_order}:</Typography>
                                <Chip
                                  label={ms.milestone_status === 'RELEASED' ? `✓ Released — ₹${fmt(ms.amount)}`
                                    : ms.milestone_status === 'CONFIRMED' ? `Confirmed — ₹${fmt(ms.amount)} (net ₹${net})`
                                    : ms.milestone_status === 'PENDING_CONFIRMATION' ? `Awaiting buyer confirm`
                                    : ms.milestone_status}
                                  size="small"
                                  color={ms.milestone_status === 'RELEASED' ? 'success'
                                    : ms.milestone_status === 'CONFIRMED' ? 'info'
                                    : ms.milestone_status === 'PENDING_CONFIRMATION' ? 'warning'
                                    : 'default'}
                                />
                                {canReleaseMilestone && (
                                  <Button size="small" variant="contained" color="success"
                                    onClick={() => setReleaseDialog({ deal, type: 'milestone', milestone: ms })}>
                                    Release ₹{fmt(ms.amount)}
                                  </Button>
                                )}
                                {ms.milestone_status === 'CONFIRMED' && !deal.bidder_has_payout_profile && (
                                  <Chip label="No payout profile" color="warning" size="small" />
                                )}
                              </Box>
                            );
                          })}
                          {milestones.every(m => m.milestone_status === 'RELEASED') && ps === 'CAPTURED' && (
                            <Alert severity="success" sx={{ mt: 0.5, py: 0.5 }}>
                              All milestones released. Deal can now be closed.
                            </Alert>
                          )}
                        </Box>
                      )}
                      {/* Mark payout done — after release */}
                      {ps === 'RELEASED' && (
                        <Box sx={{ mt: 0.5 }}>
                          {payoutDone[deal.id]?.ref ? (
                            <Chip label={`Ref: ${payoutDone[deal.id].ref}`} size="small" color="success" />
                          ) : (
                            <>
                              <Tooltip title="Mark as manually paid out (no Razorpay API call)">
                                <Button
                                  size="small" variant="contained" color="secondary"
                                  startIcon={payoutDone[deal.id]?.loading ? <CircularProgress size={14} color="inherit" /> : <PaymentIcon />}
                                  disabled={payoutDone[deal.id]?.loading}
                                  onClick={() => handleMarkPayoutDone(deal.id)}
                                  id={`btn-payout-done-${deal.id}`}
                                >
                                  Mark Paid
                                </Button>
                              </Tooltip>
                              <Tooltip title="Simulate Razorpay bank transfer to bidder's dummy account (or mock fallback if not configured)">
                                <Button
                                  size="small" variant="outlined" color="primary" sx={{ ml: 1 }}
                                  disabled={payoutDone[deal.id]?.loading}
                                  onClick={() => handleSimulatePayout(deal.id)}
                                  id={`btn-simulate-payout-${deal.id}`}
                                  startIcon={<PaymentIcon />}
                                >
                                  Rs. Simulate Razorpay
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      )}
                      {/* Close deal */}
                      {canClose && (
                        <Button size="small" variant="contained" color="primary"
                          onClick={() => setCloseDialog(deal)}>
                          Close Deal
                        </Button>
                      )}
                      {/* Payout profile warning + notify */}
                      {needsPayoutProfile && (
                        <Button size="small" variant="outlined" color="warning"
                          onClick={async () => {
                            try {
                              await apiClient.post(`/api/v1/admin/deals/${deal.id}/notify-payout/`);
                              alert('Reminder sent to bidder.');
                            } catch (e) {
                              alert(e.response?.data?.error?.message || 'Failed to send reminder.');
                            }
                          }}>
                          Notify Bidder
                        </Button>
                      )}
                      <Button size="small" variant="text"
                        onClick={() => navigate(`/admin/deals/${deal.id}`)}>
                        Details
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Release confirmation dialog */}
      <Dialog open={!!releaseDialog} onClose={() => { if (!releasing) { setReleaseDialog(null); setReleaseError(''); } }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {releaseDialog?.type === 'full' ? 'Release Full Payment' : `Release Milestone #${releaseDialog?.milestone?.sequence_order}`}
        </DialogTitle>
        <DialogContent>
          {releaseError && !releaseError.startsWith('RETENTION_ACTIVE:') && (
            <Alert severity="error" sx={{ mb: 2 }}>{releaseError}</Alert>
          )}
          {releaseError?.startsWith('RETENTION_ACTIVE:') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>⏱ Retention period is still active</Typography>
              <Typography variant="body2">
                The 2-day dispute window hasn't elapsed yet
                (eligible: {new Date(releaseError.split('RETENTION_ACTIVE:')[1]).toLocaleString()}).
                <br />
                You can still release early — this overrides the retention period.
              </Typography>
              <Button
                variant="contained" color="warning" size="small" sx={{ mt: 1 }}
                disabled={releasing}
                onClick={() => handleRelease(true)}
              >
                {releasing ? <CircularProgress size={18} color="inherit" /> : 'Release Early (Override Retention)'}
              </Button>
            </Alert>
          )}
          {releaseDialog && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Deal #{releaseDialog.deal.id}</strong> — {releaseDialog.deal.requirement_category}
              </Typography>
              {releaseDialog.type === 'full' ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    This is a <strong>non-escrow deal</strong>. You are releasing the entire deal amount to the bidder.
                  </Alert>
                  <Typography variant="body2">Gross amount: <strong>₹{fmt(releaseDialog.deal.deal_value)}</strong></Typography>
                  <Typography variant="body2">Platform fee (5%): <strong>₹{fmt(Number(releaseDialog.deal.deal_value) * 0.05)}</strong></Typography>
                  <Typography variant="body2" color="success.main">
                    Net to bidder: <strong>₹{fmt(Number(releaseDialog.deal.deal_value) * 0.95)}</strong>
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    This is an <strong>escrow deal</strong>. You are releasing funds for
                    <strong> Milestone {releaseDialog.milestone?.sequence_order} only</strong> —
                    not the full deal amount. Other milestone funds remain held until you release them separately.
                  </Alert>
                  <Typography variant="body2">Milestone: <strong>#{releaseDialog.milestone?.sequence_order} — {releaseDialog.milestone?.title}</strong></Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>Buyer confirmed: <strong>Yes (CONFIRMED)</strong></Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">Gross: <strong>₹{fmt(releaseDialog.milestone?.amount)}</strong></Typography>
                  <Typography variant="body2">Platform fee (5%): <strong>₹{fmt(Number(releaseDialog.milestone?.amount) * 0.05)}</strong></Typography>
                  <Typography variant="body2" color="success.main">
                    Net to bidder for this milestone: <strong>₹{fmt(Number(releaseDialog.milestone?.amount) * 0.95)}</strong>
                  </Typography>
                </Box>
              )}
              <Alert severity="warning" sx={{ mt: 2 }}>
                This action is <strong>irreversible</strong>. Funds will be marked as released to the bidder.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialog(null)} disabled={releasing}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => handleRelease(false)} disabled={releasing}>
            {releasing ? <CircularProgress size={20} color="inherit" /> : 'Confirm Release'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEFT verification dialog */}
      <Dialog
        open={!!neftDialog}
        onClose={() => { if (!neftVerifying) { setNeftDialog(null); setNeftError(''); setNeftRejectReason(''); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {neftDialog && isNeftPendingReview(neftDialog, neftDialog.payment_status || 'ORDER_CREATED')
            ? `Verify NEFT — Deal #${neftDialog.id}`
            : `NEFT Details — Deal #${neftDialog?.id}`}
        </DialogTitle>
        <DialogContent>
          {neftError && <Alert severity="error" sx={{ mb: 2 }}>{neftError}</Alert>}
          {neftDialog?.neft_details && (
            <Box sx={{ display: 'grid', gap: 1, mb: 2 }}>
              <Typography variant="body2"><strong>UTR / Reference:</strong> {neftDialog.neft_details.utr_number || '—'}</Typography>
              <Typography variant="body2"><strong>Amount:</strong> ₹{fmt(neftDialog.neft_details.amount || neftDialog.deal_value)}</Typography>
              <Typography variant="body2"><strong>Sender:</strong> {neftDialog.neft_details.sender_name || '—'}</Typography>
              <Typography variant="body2"><strong>Bank:</strong> {neftDialog.neft_details.sender_bank_name || '—'}</Typography>
              <Typography variant="body2"><strong>Account:</strong> {neftDialog.neft_details.sender_account_number || '—'}</Typography>
              <Typography variant="body2"><strong>IFSC:</strong> {neftDialog.neft_details.sender_ifsc || '—'}</Typography>
              {neftDialog.neft_details.transfer_date && (
                <Typography variant="body2"><strong>Transfer date:</strong> {neftDialog.neft_details.transfer_date}</Typography>
              )}
              {neftDialog.neft_details.remarks && (
                <Typography variant="body2"><strong>Remarks:</strong> {neftDialog.neft_details.remarks}</Typography>
              )}
              {neftDialog.neft_details.verification_status && (
                <Chip
                  size="small"
                  label={neftDialog.neft_details.verification_status}
                  color={
                    neftDialog.neft_details.verification_status === 'APPROVED' ? 'success'
                      : neftDialog.neft_details.verification_status === 'REJECTED' ? 'error'
                        : 'warning'
                  }
                  sx={{ width: 'fit-content' }}
                />
              )}
              {neftDialog.neft_details.rejection_reason && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Rejection reason: {neftDialog.neft_details.rejection_reason}
                </Alert>
              )}
            </Box>
          )}
          {neftDialog && isNeftPendingReview(neftDialog, neftDialog.payment_status || 'ORDER_CREATED') && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Confirm the UTR and amount match your bank records. Approving marks payment as <strong>CAPTURED</strong> and notifies both parties.
              </Alert>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Rejection reason (required if rejecting)"
                value={neftRejectReason}
                onChange={(e) => { setNeftRejectReason(e.target.value); setNeftError(''); }}
                placeholder="e.g. UTR not found in bank statement"
                disabled={neftVerifying}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNeftDialog(null)} disabled={neftVerifying}>Close</Button>
          {neftDialog && isNeftPendingReview(neftDialog, neftDialog.payment_status || 'ORDER_CREATED') && (
            <>
              <Button
                color="error"
                variant="outlined"
                disabled={neftVerifying}
                onClick={() => handleNeftVerify('REJECT')}
              >
                {neftVerifying ? <CircularProgress size={20} /> : 'Reject'}
              </Button>
              <Button
                color="success"
                variant="contained"
                disabled={neftVerifying}
                onClick={() => handleNeftVerify('APPROVE')}
              >
                {neftVerifying ? <CircularProgress size={20} color="inherit" /> : 'Approve & Capture'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Close deal confirmation dialog */}
      <Dialog open={!!closeDialog} onClose={() => !closing && setCloseDialog(null)}>
        <DialogTitle>Close Deal #{closeDialog?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Closing this deal will open the 7-day rating window for both parties.
            Make sure payment has been released before closing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialog(null)} disabled={closing}>Cancel</Button>
          <Button variant="contained" onClick={handleClose} disabled={closing}>
            {closing ? <CircularProgress size={20} color="inherit" /> : 'Close Deal'}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Simulation result snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={7000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ maxWidth: 560, wordBreak: 'break-all' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
